import React from 'react';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Image from '../../assets/Image';
import { numDifferentiation, twoDecimalNumber } from '../../utils/numbers';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useState } from 'react';
import { useDealStats } from './useDealDetails';
import Skeleton from '@mui/material/Skeleton';
import { DownloadForOfflineOutlinedIcon } from '../../assets/mui';
import { Link } from 'react-router-dom';
import DocIcon from '../../assets/doc.svg';

interface DealCompaniesProps {
  companies: {
    companyName: string;
    dealId: number;
    logoUrl: number;
    percentage: number;
  }[];
}

const DealCompanies: React.FC<DealCompaniesProps> = ({ companies }) => {
  const [expanded, setExpanded] = useState<number | false>(false);
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [isPanelLoading, setIsPanelLoading] = useState(false);

  const { data, isLoading: isRequesting } = useDealStats(expandedDealId, !expandedDealId);

  const isLoading = isPanelLoading || isRequesting;

  const {
    onTimeRepaymentsPercentage,
    totalRaisedByCompany,
    maturedDeals,
    totalDeals,
    repaymentsDone,
    totalRepayments,
    documents,
  } = data?.data || {};

  const handleChange = (panel: number, newExpanded: boolean, dealId: number) => {
    setExpanded(newExpanded ? panel : false);
    setIsPanelLoading(true);
    setTimeout(() => {
      setExpandedDealId(newExpanded ? dealId : null);
      setIsPanelLoading(false);
    }, 200);
  };

  return (
    <div className="mt-3">
      {companies.map((company, index) => (
        <MuiAccordion
          className="!bg-primary border border-stone-200 px-[14px] py-[10px]"
          expanded={expanded === index}
          onChange={(e, newExpanded) => handleChange(index, newExpanded, company.dealId)}
          disableGutters
          sx={{ '&:before': { height: '0px' } }}
          elevation={0}
        >
          <MuiAccordionSummary
            aria-controls={`panel${index}d-content`}
            id={`panel${index}d-header`}
            expandIcon={
              <KeyboardArrowDown
                className="text-green-700"
                sx={{ fontSize: '23px' }}
                data-html2canvas-ignore
              />
            }
          >
            <div
              className="flex justify-between items-center w-full"
              id="deal-detail-company-list-item"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 overflow-hidden rounded bg-zinc-200"
                  data-html2canvas-ignore
                >
                  <Image
                    // @ts-ignore
                    src={company.logoUrl as any}
                    alt={company?.companyName}
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-stone-700 text-[16px] font-[500]">
                  {company?.companyName || '-'}
                </h3>
              </div>
              <span className="text-stone-700 text-[15px] font-[500] mr-3">
                {twoDecimalNumber(company.percentage * 100)}%
              </span>
            </div>
          </MuiAccordionSummary>
          <MuiAccordionDetails>
            <div className="flex mt-auto w-full min-h-0 flex-col">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-1">
                <div>
                  <h3 className="text-stone-500 font-[600] text-[10px] uppercase mb-1 tracking-[0.8px]">
                    Deals done
                  </h3>
                  {isLoading ? (
                    <Skeleton width={80} height={40} />
                  ) : (
                    <p className="text-[16px] font-[500] text-stone-700">
                      {maturedDeals}{' '}
                      <span className="text-[16px] font-[500] text-stone-700">of {totalDeals}</span>
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-stone-500 font-[600] text-[10px] uppercase mb-1 tracking-[0.8px]">
                    Raised
                  </h3>
                  {isLoading ? (
                    <Skeleton width={80} height={40} />
                  ) : (
                    <p className="text-[16px] font-[500] text-stone-700">
                      {numDifferentiation(totalRaisedByCompany)}
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-stone-500 font-[600] text-[10px] uppercase mb-1 tracking-[0.8px]">
                    Repayments
                  </h3>
                  {isLoading ? (
                    <Skeleton width={80} height={40} />
                  ) : (
                    <p className="text-[16px] font-[500] text-stone-700">
                      {repaymentsDone} of{' '}
                      <span className="text-[16px] font-[500] text-stone-700">
                        {totalRepayments}
                      </span>
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-stone-500 font-[600] text-[10px] uppercase mb-1 tracking-[0.8px]">
                    On time payments
                  </h3>
                  {isLoading ? (
                    <Skeleton width={80} height={40} />
                  ) : (
                    <p className=" text-stone-700 text-[16px] font-[500]">
                      {onTimeRepaymentsPercentage}
                      <span className="text-[16px] font-[500] text-stone-700">%</span>
                    </p>
                  )}
                </div>
              </div>
              {/* Documents */}
              <div className="mt-9">
                <h2 className="text-neutral-500 font-[600] text-[10px] mb-[12px] uppercase">
                  Documents
                </h2>
                {isLoading ? (
                  <Skeleton width={200} height={140} />
                ) : (
                  <Link
                    to={documents?.[0]?.publicLink || ''}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className="block md:w-96"
                  >
                    <div className="border border-stone-200 md:w-96 rounded-lg py-5 px-4 flex justify-between items-center p-4">
                      {/* @ts-ignore */}
                      <Image src={DocIcon} alt="Risk Analysis" className="w-14 h-14 pr-3" />
                      <div>
                        <h4 className="text-stone-700 font-[500] text-[14px]">
                          Risk Analysis / Deep Dive
                        </h4>
                        <p className="text-stone-500 text-[12px] font-[400] mt-1">
                          Read more about how we research into companies.
                        </p>
                      </div>
                      <DownloadForOfflineOutlinedIcon className="text-green-700" />
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </MuiAccordionDetails>
        </MuiAccordion>
      ))}
    </div>
  );
};

DealCompanies.displayName = 'DealCompanies';

export default DealCompanies;
